import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import CostsTab from '../components/CostsTab';
import IncomesTab from '../components/IncomesTab';
import TopBar from '../components/TopBar';
import BreakevenCard from '../components/BreakevenCard';
import CashflowCard from '../components/CashflowCard';
import CashflowSettings from '../components/CashflowSettings';
import MeetupDetailsCard from '../components/MeetupDetailsCard';
import MeetupIncomesTab from '../components/MeetupIncomesTab';
import ScenarioPanel from '../components/ScenarioPanel';
import {
  CONFERENCE_COST_PLAN_KEYS,
  CONFERENCE_REVENUE_PLAN_KEYS,
  MEETUP_COST_PLAN_KEYS,
  MEETUP_REVENUE_PLAN_KEYS,
} from '../lib/cashflow-defaults';
import { costCategoriesFor } from '../lib/types';
import { computeBreakeven, statusLine } from '../lib/breakeven';
import { calculate } from '../lib/calc';
import { formatMoney } from '../lib/currency';
import { emptyEventData, emptyMeetupEventData } from '../lib/defaults';
import { exportEventToExcel } from '../lib/export-excel';
import { exportEventToPdf } from '../lib/export-pdf';
import { exportEventToPptx } from '../lib/export-pptx';
import { applyScenario, SCENARIO_LABELS } from '../lib/scenarios';
import type { EventData, EventMeta, EventType, Scenario } from '../lib/types';

type TabKey = 'income' | 'costs' | 'breakeven' | 'cashflow';

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [meta, setMeta] = useState<EventMeta>({
    name: '',
    event_type: 'conference',
    country_code: '',
    city: '',
    start_date: null,
    end_date: null,
    conference_days: 1,
    workshop_days: 0,
    currency: 'EUR',
    meetup_meta: {},
  });
  const [data, setData] = useState<EventData>(emptyEventData());
  const [tab, setTab] = useState<TabKey>('income');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const ev = await api.getEvent(id);
        if (cancelled) return;
        setMeta({
          id: ev.id,
          name: ev.name,
          event_type: ev.event_type === 'meetup' ? 'meetup' : 'conference',
          country_code: ev.country_code ?? '',
          city: ev.city ?? '',
          start_date: ev.start_date ?? null,
          end_date: ev.end_date ?? null,
          conference_days: ev.conference_days,
          workshop_days: ev.workshop_days,
          currency: ev.currency,
          meetup_meta: ev.meetup_meta ?? {},
        });
        setData(normalizeData(ev.data, ev.event_type === 'meetup' ? 'meetup' : 'conference'));
        setDirty(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load event');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The scenario is a projection over EventData. Headline forecast and
  // break-even are computed against the projected (effective) data; the
  // IncomesTab / CostsTab continue to show and edit the raw plan.
  //
  // Meetups skip the scenario projection entirely — a single-evening event
  // doesn't need pessimistic/optimistic modelling, so we always use the
  // raw data regardless of what scenario.active happens to be.
  const effectiveData = useMemo(
    () =>
      meta.event_type === 'meetup' ? data : applyScenario(data),
    [data, meta.event_type],
  );
  const totals = useMemo(() => calculate(effectiveData), [effectiveData]);
  // Summary tile mirrors VAT mode: in gross mode every displayed amount is
  // grossed up by the rate uniformly, so the badge ("Gross") matches the
  // numbers underneath. Net mode is the identity.
  const grossFactor =
    data.vatMode === 'gross' ? 1 + data.vatRate : 1;
  const display = useMemo(
    () => ({
      net: { min: totals.net.min * grossFactor, max: totals.net.max * grossFactor },
      revenue: {
        min: totals.revenue.min * grossFactor,
        max: totals.revenue.max * grossFactor,
      },
      costs: totals.costs * grossFactor,
      perAttendeeRevenue: {
        min: totals.perAttendee.revenue.min * grossFactor,
        max: totals.perAttendee.revenue.max * grossFactor,
      },
      perAttendeeCost: totals.perAttendee.cost * grossFactor,
    }),
    [totals, grossFactor],
  );
  const breakeven = useMemo(() => computeBreakeven(effectiveData), [effectiveData]);
  const beStatus = useMemo(
    () => statusLine(breakeven, meta.currency),
    [breakeven, meta.currency],
  );
  const scenarioActive = data.scenario.active;
  const scenarioBadge =
    meta.event_type === 'meetup' || scenarioActive === 'realistic'
      ? null
      : SCENARIO_LABELS[scenarioActive];

  function setScenario(s: Scenario) {
    patchData((d) => ({ ...d, scenario: s }));
  }

  // Meetups deliberately don't ship Break-even / Cashflow tabs (single
  // evening, no payment-timing complexity). If the user happened to be on
  // one of those tabs and switches to a meetup, fall back to Income.
  const isMeetup = meta.event_type === 'meetup';
  const effectiveTab: TabKey =
    isMeetup && (tab === 'breakeven' || tab === 'cashflow') ? 'income' : tab;

  async function save() {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateEvent(id, {
        name: meta.name,
        event_type: meta.event_type,
        country_code: meta.country_code,
        city: meta.city,
        start_date: meta.start_date ?? null,
        end_date: meta.end_date ?? null,
        conference_days: meta.conference_days,
        workshop_days: meta.workshop_days,
        currency: meta.currency,
        meetup_meta: meta.meetup_meta ?? {},
        data,
      });
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function exportToExcel() {
    setError(null);
    try {
      await exportEventToExcel(meta, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  }

  async function exportToPptx() {
    setError(null);
    try {
      await exportEventToPptx(meta, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  }

  async function exportToPdf() {
    setError(null);
    try {
      await exportEventToPdf(meta, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  }

  function patchMeta(p: Partial<EventMeta>) {
    setMeta((m) => ({ ...m, ...p }));
    setDirty(true);
  }
  function patchData(fn: (d: EventData) => EventData) {
    setData((d) => fn(d));
    setDirty(true);
  }

  if (loading) {
    return <div className="container">Loading…</div>;
  }

  return (
    <div className="app">
      <TopBar
        meta={meta}
        onChange={patchMeta}
        vatMode={data.vatMode}
        vatRate={data.vatRate}
        onVatModeChange={(mode) => patchData((d) => ({ ...d, vatMode: mode }))}
        onVatRateChange={(rate) => patchData((d) => ({ ...d, vatRate: rate }))}
        email={user?.email}
        saving={saving}
        saved={!dirty && !saving}
        onSave={save}
        onExportXlsx={exportToExcel}
        onExportPptx={exportToPptx}
        onExportPdf={exportToPdf}
        onLogout={async () => {
          await logout();
          navigate('/login');
        }}
      />

      <div className="container">
        {error && (
          <div className="error-banner mb-12">
            {error}
          </div>
        )}

        {meta.event_type === 'meetup' && (
          <MeetupDetailsCard meta={meta} onChange={patchMeta} />
        )}

        <div className="summary">
          <div className="kicker">
            Net (revenue − cost)
            <span className="vat-badge">{data.vatMode === 'net' ? 'Net of VAT' : 'Gross'}</span>
            {scenarioBadge && (
              <span className="scenario-badge">Scenario: {scenarioBadge}</span>
            )}
          </div>
          <div className="value">
            <span className={display.net.min >= 0 ? 'pos' : 'neg'}>
              {formatMoney(display.net.min, meta.currency)}
            </span>
            {display.net.min !== display.net.max && (
              <>
                {'  –  '}
                <span className={display.net.max >= 0 ? 'pos' : 'neg'}>
                  {formatMoney(display.net.max, meta.currency)}
                </span>
              </>
            )}
          </div>
          <div className="meta">
            <span>{totals.ticketCount} tickets</span>
            <span>·</span>
            <span>{totals.sponsorCount} sponsors</span>
            <span>·</span>
            <span>{totals.addonCount} add-ons</span>
          </div>

          {beStatus && (
            <div className={`be-status ${beStatus.tone}`}>
              {beStatus.text}
            </div>
          )}

          <div className="summary-stats">
            <div className="summary-stat">
              <div className="stat-label">Revenue</div>
              <div className="stat-value revenue">
                {display.revenue.min === display.revenue.max
                  ? formatMoney(display.revenue.min, meta.currency)
                  : `${formatMoney(display.revenue.min, meta.currency)} – ${formatMoney(
                      display.revenue.max,
                      meta.currency,
                    )}`}
              </div>
            </div>
            <div className="summary-stat">
              <div className="stat-label">Estimated cost</div>
              <div className="stat-value cost">
                {formatMoney(display.costs, meta.currency)}
              </div>
            </div>
          </div>

          {totals.ticketCount > 0 && (
            <div className="summary-stats summary-stats-secondary">
              <div className="summary-stat">
                <div className="stat-label">Revenue per attendee</div>
                <div className="stat-value">
                  {display.perAttendeeRevenue.min === display.perAttendeeRevenue.max
                    ? formatMoney(display.perAttendeeRevenue.min, meta.currency)
                    : `${formatMoney(display.perAttendeeRevenue.min, meta.currency)} – ${formatMoney(
                        display.perAttendeeRevenue.max,
                        meta.currency,
                      )}`}
                </div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Cost per attendee</div>
                <div className="stat-value cost">
                  {formatMoney(display.perAttendeeCost, meta.currency)}
                </div>
              </div>
            </div>
          )}
        </div>

        {!isMeetup && <ScenarioPanel data={data} onChange={setScenario} />}

        <div className="tabs">
          <button
            className={`tab ${effectiveTab === 'income' ? 'active' : ''}`}
            onClick={() => setTab('income')}
          >
            Incomes
          </button>
          <button
            className={`tab ${effectiveTab === 'costs' ? 'active' : ''}`}
            onClick={() => setTab('costs')}
          >
            Costs
          </button>
          {!isMeetup && (
            <>
              <button
                className={`tab ${effectiveTab === 'breakeven' ? 'active' : ''}`}
                onClick={() => setTab('breakeven')}
              >
                Break-even
              </button>
              <button
                className={`tab ${effectiveTab === 'cashflow' ? 'active' : ''}`}
                onClick={() => setTab('cashflow')}
              >
                Cashflow
              </button>
            </>
          )}
        </div>

        {effectiveTab === 'income' && (
          meta.event_type === 'meetup' ? (
            <MeetupIncomesTab
              data={data}
              currency={meta.currency}
              vatMode={data.vatMode}
              vatRate={data.vatRate}
              onChange={patchData}
            />
          ) : (
            <IncomesTab
              data={data}
              currency={meta.currency}
              vatMode={data.vatMode}
              vatRate={data.vatRate}
              showAddons={true}
              onChange={patchData}
            />
          )
        )}
        {effectiveTab === 'costs' && (
          <CostsTab
            data={data}
            currency={meta.currency}
            vatMode={data.vatMode}
            vatRate={data.vatRate}
            categories={costCategoriesFor(meta.event_type)}
            onChange={patchData}
          />
        )}
        {effectiveTab === 'breakeven' && !isMeetup && (
          <BreakevenCard
            data={effectiveData}
            currency={meta.currency}
            vatMode={data.vatMode}
          />
        )}
        {effectiveTab === 'cashflow' && !isMeetup && (
          <>
            <CashflowSettings
              data={data}
              revenueKeys={
                meta.event_type === 'meetup'
                  ? MEETUP_REVENUE_PLAN_KEYS
                  : CONFERENCE_REVENUE_PLAN_KEYS
              }
              costKeys={
                meta.event_type === 'meetup'
                  ? MEETUP_COST_PLAN_KEYS
                  : CONFERENCE_COST_PLAN_KEYS
              }
              onChange={(plans) =>
                patchData((d) => ({ ...d, cashflowPlans: plans }))
              }
            />
            <CashflowCard
              data={effectiveData}
              eventDateISO={meta.start_date}
              currency={meta.currency}
              vatMode={data.vatMode}
            />
          </>
        )}
      </div>
    </div>
  );
}

type RawSponsor = {
  id?: string;
  name?: string;
  qty?: number;
  price?: number;
  priceMin?: number;
  priceMax?: number;
};

// Defensive: tolerate older / partial saved data, including sponsors that
// were saved before the min/max split, and events saved before scenarios
// existed. The fallback defaults differ per template — a meetup with
// missing tickets/sponsors should NOT hydrate with conference defaults
// (Early Bird / Standard / VIP and Bronze/Silver/Gold/Platinum).
function normalizeData(raw: unknown, eventType: EventType = 'conference'): EventData {
  const empty = eventType === 'meetup' ? emptyMeetupEventData() : emptyEventData();
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;

  const rawSponsors = Array.isArray(r.sponsors)
    ? (r.sponsors as RawSponsor[])
    : [];
  const sponsors = rawSponsors.length
    ? rawSponsors.map((s, i) => ({
        id: s.id ?? `s_${i}`,
        name: s.name ?? 'Tier',
        qty: s.qty ?? 0,
        priceMin: s.priceMin ?? s.price ?? 0,
        priceMax: s.priceMax ?? s.price ?? 0,
      }))
    : empty.sponsors;

  const tickets = Array.isArray(r.tickets) && r.tickets.length
    ? (r.tickets as EventData['tickets'])
    : empty.tickets;
  const addons = Array.isArray(r.addons) && r.addons.length
    ? (r.addons as EventData['addons'])
    : empty.addons;
  const costs = (r.costs ?? {}) as Partial<EventData['costs']>;
  const scenario = normalizeScenario(r.scenario);
  const cashflowPlans =
    r.cashflowPlans && typeof r.cashflowPlans === 'object'
      ? (r.cashflowPlans as EventData['cashflowPlans'])
      : {};
  const vatMode: EventData['vatMode'] = r.vatMode === 'gross' ? 'gross' : 'net';
  const vatRate =
    typeof r.vatRate === 'number' && r.vatRate >= 0 && r.vatRate <= 2
      ? r.vatRate
      : 0.23;
  return {
    tickets,
    sponsors,
    addons,
    costs: {
      venue: costs.venue ?? [],
      catering: costs.catering ?? [],
      security: costs.security ?? [],
      av: costs.av ?? [],
      marketing: costs.marketing ?? [],
      additional: costs.additional ?? [],
      refreshments: costs.refreshments ?? [],
      food: costs.food ?? [],
      swags: costs.swags ?? [],
      materials: costs.materials ?? [],
      speakers: costs.speakers ?? [],
    },
    scenario,
    cashflowPlans,
    vatMode,
    vatRate,
  };
}

function normalizeScenario(raw: unknown): EventData['scenario'] {
  const fallback = emptyEventData().scenario;
  if (!raw || typeof raw !== 'object') return fallback;
  const r = raw as Record<string, unknown>;
  const active =
    r.active === 'pessimistic' || r.active === 'optimistic' ||
    r.active === 'custom' || r.active === 'realistic'
      ? r.active
      : 'realistic';
  const custom = (r.custom ?? {}) as Record<string, unknown>;
  return {
    active,
    custom: {
      ticketFill:
        (custom.ticketFill && typeof custom.ticketFill === 'object'
          ? (custom.ticketFill as Record<string, number>)
          : {}),
      sponsorFill:
        (custom.sponsorFill && typeof custom.sponsorFill === 'object'
          ? (custom.sponsorFill as Record<string, number>)
          : {}),
      addonsEnabled:
        custom.addonsEnabled === 'all-on' || custom.addonsEnabled === 'all-off'
          ? custom.addonsEnabled
          : 'as-planned',
      costMultiplier:
        typeof custom.costMultiplier === 'number' ? custom.costMultiplier : 1.0,
    },
  };
}
